import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { LaunchpoolStatus } from '@flipster/apis/v1/launchpool/types';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { useV1Currencies } from '@flipster/features/src/shared/currency/use-v1-currencies';
import { launchpoolItemBunja, useLaunchpool } from '@flipster/features/src/shared/launchpool/state';
import { useRouter } from '@flipster/i18n/routing';
import { TextButton } from '@flipster/react/design-system';
import { snackbar } from '@flipster/react/design-system/snackbar';
import Formatted from '@flipster/react/design-utils/formatted';
import DocumentHistoryIcon from '@flipster/react/icon/generated/DocumentHistoryIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css, cx } from '@flipster/styled-system/css';
import { center, flex, hstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { alert } from '@flipster/utils/alert';
import segment from '@flipster/utils/segment';
import { StakeEvent } from '@flipster/utils/segment/events/stake';

import { closedColorCssRaw, dividerCssRaw } from '@web/app/[locale]/(web)/launchpool/_lists/_Item/shared-css';

const TotalReward = () => {
  const router = useRouter();

  const { isLoggedIn } = useAuth();
  const { currenciesKeyByCurrency } = useV1Currencies();
  const { launchpoolItemAtom } = useBunja(launchpoolItemBunja);
  const launchpool = useAtomValue(launchpoolItemAtom);
  const { postLaunchPoolUnstakeApiMutation } = useLaunchpool();

  const { id, stakedAmount, stakingCurrency, rewardCurrency, estimatedReward, status, isUnstakable } = launchpool;
  const rewardCurrencyPrecision = currenciesKeyByCurrency?.[rewardCurrency]?.blockchainInfo[0]?.withdrawalPrecision;

  const isUpcoming = status === LaunchpoolStatus.Upcoming;
  const isClosed = status === LaunchpoolStatus.Closed;
  const isShowLaunchpoolHistory = status !== LaunchpoolStatus.Upcoming && Number(stakedAmount) !== 0;

  const postUnstake = async (currency: string) => {
    segment.track(StakeEvent.UnstakeConfirmClick, { symbol: currency });
    try {
      await postLaunchPoolUnstakeApiMutation.mutateAsync(id);
      snackbar.success({ message: `Unstaked in ${currency}` });
    } catch (error) {
      alert.error(error);
    }
  };

  const handleClickUnstake = async () => {
    segment.track(StakeEvent.UnstakeTextLinkClick, { symbol: stakingCurrency });
    if (isUpcoming) {
      await postUnstake(stakingCurrency);
      return;
    }

    alert.show({
      title: 'Unstake',
      message: (
        <p className={css({ color: 'content.state.warning' })}>
          If you unstake now, your rewards will be reset to 0. Would you like to proceed?
        </p>
      ),
      onConfirm: () => postUnstake(stakingCurrency),
      confirmText: 'Unstake',
      closeText: 'Cancel',
      withClose: true,
    });
  };

  const onClickLaunchpoolHistory = () => {
    router.push(`/history/transaction/launchpool-reward?launchpoolId=${id}`);
  };

  const onClickRewardInfoButton = () => {
    alert.show({
      title: 'Your total reward',
      message: (
        <div className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-3' })}>
          <p>
            Rewards are calculated proportional to your share of the total staked amount and the trading multiplier
            (daily trading volume or VIP status).
          </p>
          <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-4', mt: 16, mb: 4 })}>Period</p>
          <p>Refreshed daily at 00:00 UTC.</p>
          <p>There may be a delay of a few minutes.</p>
          <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-4', mt: 16, mb: 4 })}>
            Duration
          </p>
          <p>
            If staking is maintained until the end, rewards will be paid out. If you unstake early, no rewards will be
            earned.
          </p>
        </div>
      ),
      confirmText: 'OK',
    });
  };

  return (
    <>
      <div className={css(dividerCssRaw, { mt: 16, mb: 12 })} />
      <div>
        <div className={css({ pos: 'relative' })}>
          <button
            type='button'
            onClick={onClickLaunchpoolHistory}
            disabled={!isShowLaunchpoolHistory}
            className={center({
              pos: 'absolute',
              top: 0,
              right: 0,
              color: 'content.default.level-3',
              _disabled: { color: 'content.interaction.disabled' },
            })}
          >
            <DocumentHistoryIcon size={24} />
          </button>

          <div
            className={cx(
              css({
                display: 'flex',
                flexDirection: { base: 'column', tablet: 'row' },
                justifyContent: 'center',
                alignItems: { tablet: 'center' },
                gap: { base: 8, tablet: 12 },
                color: 'content.default.level-4',
              }),
              isClosed && css(closedColorCssRaw),
            )}
          >
            <div className={hstack({ textStyle: 'Text/M/Strong', h: 24 })}>
              Your total reward
              <button
                type='button'
                onClick={onClickRewardInfoButton}
                className={css({ buttonShape: 'inline-icon', ml: 4 })}
              >
                <InformationCircleIcon size={16} color={token('colors.content.default.level-1')} />
              </button>
            </div>
            <div>
              <Formatted.Number
                value={estimatedReward}
                suffix={<span className={css({ textStyle: 'Text/M/Regular' })}>{rewardCurrency}</span>}
                precision={rewardCurrencyPrecision}
                zeroString='-'
                css={hstack.raw({
                  textStyle: 'Title/S',
                  justify: { tabletDown: 'flex-end' },
                })}
              />
            </div>
          </div>
          {!isClosed && (
            <div className={flex({ justify: 'end', py: 8 })}>
              <TextButton
                size='medium'
                color='passive'
                onClick={handleClickUnstake}
                disabled={postLaunchPoolUnstakeApiMutation.isPending || !isLoggedIn || !isUnstakable}
                label='Unstake'
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TotalReward;
